import { autorun, makeAutoObservable, runInAction, toJS } from 'mobx';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { RootStore } from 'store/rootStore';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { Cart } from 'types/cartService';
import { CopyHelper } from 'global/utils/CopyHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Portfolio } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { PortfolioRequest } from 'types/productService';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { ContactInfo, Group, GroupSource } from 'types/groupService';
import { dateWithTime } from 'global/utils/dateWithTime';
import { EnrollType } from 'types/enrollmentService';
import ProductLineModule from 'store/modules/productLineModule';
import { getPortfolios } from 'api/operations/productServiceV2';

export const INITIAL_BROWSE_INFO = {
  effectiveDate: '',
  zipCode: '',
  state: '',
  county: '',
};

interface FormFields extends DefaultFormFields {
  companyName: FormControl<string>;
  numberOfEmployees: FormControl<string>;
  workingWithAgent: FormControl<string>;
  // Main contact info
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  // Physical Address (Optional)
  street: FormControl<string>;
  street2: FormControl<string>;
  city: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  zipCode: FormControl<string>;
  notes: FormControl<string>;
}

export type PortfolioByProductLine = {
  [key in ProductLineId]: Portfolio[];
};

export class ContactMeModalStore {
  rootStore: RootStore;
  productLineModule: ProductLineModule;

  form: FormController<FormFields>;
  loading = false;
  selectedProductLineIds: ProductLineId[] = [];
  cart: Cart;
  // Portfolios are needed to display the logo on the products that are added to the cart
  allPortfolios: PortfolioByProductLine[] = [];
  // For zip code component
  ineligibleState = false;
  group: Group = {
    ...INITIAL_GROUP_OBJECT,
  };

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public async requestZipCodeInfo(zipCode: string): Promise<void> {
    await this.rootStore.geoStore.requestZipCodeInfo(zipCode);
    runInAction(() => {
      const { wrongZipCodeKeyedIn, zipCodeAffiliatedData } = this.rootStore.geoStore;
      if (!wrongZipCodeKeyedIn) {
        this.form.fields.state.value = zipCodeAffiliatedData.state.id;
        this.form.fields.county.value = zipCodeAffiliatedData.counties[0].name;
        this.ineligibleState = !this.rootStore.domainStore.isEligibleState(zipCodeAffiliatedData.state.id);
      }
    });
  }

  // If there is no cart, bookmarked plans block will not be rendered
  public async collectCartData(cartId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.cartStore.getCart(cartId);
    await runInAction(async () => {
      // Copy basic types to avoid possible override
      const cartData = CopyHelper.deepCopy(res.data.getCart);
      this.cart = cartData;
      const convertedCart = toJS(cartData);
      const { zipCode, numberOfEmployees } = convertedCart.lastUpdateBrowsingCriteria;
      this.form.fields.zipCode.overrideValue(zipCode);
      this.form.fields.numberOfEmployees.overrideValue(numberOfEmployees || '');

      await this.getProductLines(convertedCart);
      await runInAction(async () => {
        const sponsorId = this.rootStore.domainStore.domainHeader.id;

        const usedPLineIds = [...new Set(convertedCart.cartItems.map(({ productLineId }) => productLineId))];
        this.selectedProductLineIds = [...usedPLineIds];

        // Get logo for Bookmarked cards that are added to cart
        const requests = usedPLineIds.map(async (productLineId) => {
          return this.getPortfolios({
            // Set the CartBrowsingCriteria type to use the default browsing criteria that will be set in BE.
            bc: convertedCart.lastUpdateBrowsingCriteria,
            rateSegment: convertedCart.rateSegment,
            productLineId,
            sponsorId,
            agencyId: '',
            groupId: convertedCart.groupId,
          });
        });

        await Promise.all([this.getGroupById(convertedCart.groupId), ...requests]).then(() => {
          this.setLoading(false);
        });
      });
    });
  }

  private async getGroupById(id: string): Promise<void> {
    const res = await this.rootStore.groupStore.getGroupByIdCached(id);
    runInAction(() => {
      this.group = CopyHelper.deepCopy(res.data.groupById);
    });
  }

  private async getPortfolios(request: PortfolioRequest): Promise<void> {
    const productLineId = request.productLineId;
    const res = await getPortfolios(request);
    runInAction(() => {
      // Make checkboxes checked by default for product lines that have added to cart plans
      this.onSelectedProductLineChange(productLineId);
      this.allPortfolios.push({ [productLineId]: res.data.getPortfolios } as PortfolioByProductLine);
    });
  }

  private async getProductLines(cart: Cart): Promise<void> {
    await this.productLineModule.getProductLines(cart, this.rootStore.domainStore.domainHeader.id);
  }

  public get addedProductLineIdsToCart(): ProductLineId[] {
    return [...new Set(this.cart.cartItems.map((cartItem) => cartItem.productLineId))];
  }

  public checkCarrierProductLinesRules(): Record<string, string>[] {
    return this.productLineModule.checkCarrierProductLinesRules(this.addedProductLineIdsToCart);
  }

  public allSortedProductLineIds(): ProductLineId[] {
    return this.productLineModule.allSortedProductLineIds;
  }

  public isProductLineSelected(productLineId: ProductLineId): boolean {
    return this.selectedProductLineIds.includes(productLineId);
  }

  public onSelectedProductLineChange(productLineId: ProductLineId): void {
    if (this.selectedProductLineIds.includes(productLineId)) {
      this.selectedProductLineIds = this.selectedProductLineIds.filter((id) => id !== productLineId);
    } else {
      this.selectedProductLineIds.push(productLineId);
    }
  }

  private resetInitialData(): void {
    this.productLineModule = new ProductLineModule();
    this.selectedProductLineIds = [];
    this.cart = INITIAL_CART_OBJECT;
    this.allPortfolios = [];
  }

  public async submitForm(onClose: () => void): Promise<void> {
    this.setLoading(true);

    const companyName = this.form.fields.companyName.value;
    const inputGroup = {
      id: this.group.id,
      name: companyName,
      source: GroupSource.ESTORE,
      effectiveDate: this.cart.lastUpdateBrowsingCriteria.effectiveDate,
      contactInfo: this.contactInfo,
      notes: this.combinedNotes,
      agentOfRecord: null,
    };

    const sponsorId = this.rootStore.domainStore.domainHeader.id;
    const inputProductLines = this.rootStore.enrollmentStore.convertNewSaleCartIntoProductLine(this.cart);
    const cartName = `${companyName} ${dateWithTime()}`;

    const res = await this.rootStore.cartStore.copyCart(this.cart.id);
    runInAction(() => {
      const newCart = res.data?.copyCart;
      if (newCart) {
        const requests: unknown[] = [
          this.rootStore.groupStore.updateGroup(inputGroup, sponsorId, newCart.id, false),
          this.rootStore.cartStore.saveProposal(newCart.id, cartName),
        ];
        if (this.cart.cartItems.length > 0) {
          requests.push(
            this.rootStore.enrollmentStore.saveProposal(
              this.group.id,
              newCart,
              cartName,
              inputProductLines,
              sponsorId,
              EnrollType.CONTACT_ME,
            ),
          );
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        Promise.all(requests)
          .then(() => {
            this.rootStore.notificationModalStore.openNotificationModal({
              title: 'A team member has been notified to contact you!',
              subtitle: `We've sent you an email with a summary of your information and whom to contact if you don't hear back
                within <b>1-2 business days</b>`,
              type: 'CONTACT_ME',
            });
          })
          .catch(() => {
            this.rootStore.notificationModalStore.openNotificationModal({
              title: 'Something went wrong',
              subtitle: 'Please try again',
              type: 'ERROR',
            });
          })
          .finally(() => {
            onClose();
            this.setLoading(false);
            this.form.resetForm();
            this.resetInitialData();
          });
      }
    });
  }

  private get contactInfo(): ContactInfo {
    const { firstName, lastName, email, phone, city, street, street2, zipCode, state, county } = this.form.fields;
    return {
      firstName: firstName.value,
      lastName: lastName.value,
      primaryPhone: phone.value,
      primaryEmail: email.value,
      primaryAddress: {
        city: city.value,
        street: street.value,
        street2: street2.value,
        zip: zipCode.value,
        state: state.value,
        county: county.value,
      },
    };
  }

  private get combinedNotes(): string {
    const { notes, numberOfEmployees, workingWithAgent } = this.form.fields;

    const selectedProductLineIds =
      this.selectedProductLineIds.length > 0
        ? this.selectedProductLineIds.map((pLineId) => PRODUCT_LINES_CONTENT_MAP[pLineId].label).join(', ')
        : '-';

    return `
    Number Of Employees: ${numberOfEmployees.value}\n
    Working with Agent: ${workingWithAgent.value}\n
    Interested Product Lines: ${selectedProductLineIds || '-'}\n
    Interested Plans: ${this.plansNamesByProductLine || '-'}\n
    Message from Group Admin: ${notes.value || '-'}
    `;
  }

  private get plansNamesByProductLine(): string {
    const interestedPlanNamesByPL = toJS(this.cart).cartItems.reduce((acc, { productLineId, portfolioName }) => {
      if (acc[productLineId]) {
        acc[productLineId].push(portfolioName);
      } else {
        acc[productLineId] = [portfolioName];
      }
      return acc;
    }, {} as { [key in ProductLineId]: string[] });

    return Object.keys(interestedPlanNamesByPL)
      .map(
        (productLineId) =>
          `${PRODUCT_LINES_CONTENT_MAP[productLineId as ProductLineId].label}: ${interestedPlanNamesByPL[
            productLineId as ProductLineId
          ].join(', ')}`,
      )
      .join('; ');
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cart = INITIAL_CART_OBJECT;
    this.productLineModule = new ProductLineModule();

    this.form = new FormController({
      companyName: new FormControl<string>('', { validations: ['printableCharacters'] }),
      numberOfEmployees: new FormControl<string>('', { validations: ['containsOnlyNumbers'] }),
      workingWithAgent: new FormControl<string>(''),
      // Main contact info
      firstName: new FormControl<string>('', { validations: ['printableCharacters'] }),
      lastName: new FormControl<string>('', { validations: ['printableCharacters'] }),
      email: new FormControl<string>('', { validations: ['isEmailValid'] }),
      phone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
      // Physical Address (Optional)
      city: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      street: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['street2'],
      }),
      street2: new FormControl<string>('', { optional: true }),
      zipCode: new FormControl<string>('', {
        validations: ['containsOnlyNumbers'],
        optional: true,
        requiredWithFields: ['state', 'county'],
      }),
      state: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['zipCode', 'county'],
      }),
      county: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['zipCode', 'state'],
      }),
      notes: new FormControl<string>('', { optional: true }),
    });

    // Run a request for zip code input (auto-uploading a county and a state)
    autorun(() => {
      if (this.form.fields.zipCode.value.length === 5) {
        void this.requestZipCodeInfo(this.form.fields.zipCode.value);
      } else {
        this.form.fields.county.overrideValue('');
        this.form.fields.state.overrideValue('');
      }
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
